import React from 'react';
import { XMarkIcon, PencilIcon, PlusIcon } from '@heroicons/react/24/outline';

// Task 24/41/67/69/39/80 (owner requirements, see ShipmentPlanner.js history): the box
// configuration modal -- two-tier box model (owner, verbatim: "the user should specify the
// weight of the unit, and also specify the weight and qty of boxes to make the shipment. and
// the total is added together ... the box size and weights can be shared across asins, the
// shipment configuration should be asin specific though"). Extracted verbatim out of
// ShipmentPlanner.js so PO Creation can open the same modal instead of a second
// implementation of the same UNIT/CARTON semantics. Purely presentational -- the caller
// (ShipmentPlanner.js) owns every piece of state; this component only renders it.
export default function BoxConfigModal({
    packagingPopoverAsin,
    packagingPopoverTitle,
    packagingPopoverQty,
    packagingForm,
    packagingFieldErrors,
    packagingFormValid,
    packagingAttemptedSubmit,
    packagingStyleChoice,
    packagingSaving,
    packagingLoadedLbs,
    packagingSummaryLine,
    packagingModalActivePkg,
    packagingModalDerived,
    packagingModalLegacyOnly,
    packagingModalTotals,
    boxCatalog,
    choosePackagingStyle,
    closePackagingPopover,
    applyPackaging,
    updatePackagingField,
    fmtLbsShort,
    newBoxOpen,
    newBoxForm,
    newBoxSaving,
    editingBoxId,
    boxError,
    boxNote,
    selectedBox,
    updateNewBoxField,
    saveNewBoxToCatalog,
    renderCatalogBoxCard,
    setEditingBoxId,
    setNewBoxForm,
    setBoxError,
    setNewBoxOpen,
    setSelectedBoxId,
    // Finding 2 (task-3 report): on a host with no shipment (PO Creation), "Use for this
    // shipment" describes something that does not exist -- both footer buttons used to be wired
    // to the same persisting handler there, so that label was simply untrue. When true, render
    // only the persisting action ("Use and make default for {ASIN}") and drop the non-persisting
    // one instead of mislabeling it. Default false renders both buttons exactly as before --
    // ShipmentPlanner never passes this prop, so its rendering is unchanged.
    primaryActionOnly = false,
    // Owner decision (task 3): an ASIN whose allowed_modes designation is explicitly BOTH must not
    // silently inherit either weight ceiling -- the host asks which mode this box is being
    // configured for instead of guessing. All three props default to "no picker" so
    // ShipmentPlanner, which never passes them, renders byte-for-byte unchanged; PO Creation opts
    // in only for a BOTH-designated ASIN. shipmentModeChoice is null until the operator picks --
    // nothing is pre-selected -- and the host's own useBoxConfig call (shipmentMode: null) is what
    // actually withholds the ceiling; this picker is just how the choice gets made.
    shipmentModePickerVisible = false,
    shipmentModeChoice = null,
    onChooseShipmentMode = () => {},
}) {
    return (
            packagingPopoverAsin && (
                // Task 69 owner addition: fit-to-content modal -- no inner scroll region; the
                // overlay scrolls browser-natively when the panel outgrows the viewport.
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center z-50 p-4 overflow-y-auto">
                    {/* Finding C/5 (final review): a stable, explicit hook for a host's outside-
                        click detection -- the panel used to be located by DOM position
                        (':scope > div > div'), which silently breaks if this component's markup
                        ever grows a wrapper. data-testid never depends on nesting depth. */}
                    <div data-testid="box-config-panel" className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-8">
                        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
                            <div className="min-w-0">
                                <h3 className="text-sm font-semibold text-gray-900">Box configuration</h3>
                                {/* Task 39 (owner, with screenshot): title alongside the ASIN when the calling
                                    row has one -- falls back to ASIN-only when it doesn't. */}
                                <p className="text-xs text-gray-500 mt-0.5 truncate">
                                    {packagingPopoverTitle && (
                                        <>
                                            <span className="text-gray-700">{packagingPopoverTitle}</span>
                                            <span className="mx-1 text-gray-300">&middot;</span>
                                        </>
                                    )}
                                    <span className="font-mono">{packagingPopoverAsin}</span>
                                </p>
                            </div>
                            <button onClick={closePackagingPopover} className="flex-shrink-0 text-gray-400 hover:text-gray-600">
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="px-5 py-4 space-y-5">
                            {/* ---- Task 67 (owner: "maybe a toggle that would allow for carton vs
                                single unit 'boxes'?"): the two packaging styles, explicit. ---- */}
                            <div className="grid grid-cols-2 gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
                                <button
                                    onClick={() => choosePackagingStyle('UNIT')}
                                    className={`rounded-md px-2 py-2 text-xs font-medium text-center transition-colors ${
                                        packagingStyleChoice === 'UNIT'
                                            ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-500'
                                            : 'text-gray-600 hover:text-gray-800'}`}
                                >
                                    Single unit
                                    <span className="block text-[10px] font-normal text-gray-500">the unit ships in its own box</span>
                                </button>
                                <button
                                    onClick={() => choosePackagingStyle('CARTON')}
                                    className={`rounded-md px-2 py-2 text-xs font-medium text-center transition-colors ${
                                        packagingStyleChoice === 'CARTON'
                                            ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-500'
                                            : 'text-gray-600 hover:text-gray-800'}`}
                                >
                                    Carton
                                    <span className="block text-[10px] font-normal text-gray-500">multiple units per box</span>
                                </button>
                            </div>

                            {/* ---- Owner decision (task 3): a BOTH-designated ASIN's shipment
                                mode picker -- nothing pre-selected. Until the operator picks, no
                                ceiling is assumed anywhere below (the host's useBoxConfig call
                                carries shipmentMode: null, which blocks packagingFormValid via
                                errors.mode) and this explains why in plain language. ---- */}
                            {shipmentModePickerVisible && (
                                <div data-testid="shipment-mode-picker">
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                        Shipment mode
                                    </h4>
                                    <div className="grid grid-cols-2 gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
                                        <button
                                            type="button"
                                            onClick={() => onChooseShipmentMode('UPS')}
                                            className={`rounded-md px-2 py-2 text-xs font-medium text-center transition-colors ${
                                                shipmentModeChoice === 'UPS'
                                                    ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-500'
                                                    : 'text-gray-600 hover:text-gray-800'}`}
                                        >
                                            UPS
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onChooseShipmentMode('LTL')}
                                            className={`rounded-md px-2 py-2 text-xs font-medium text-center transition-colors ${
                                                shipmentModeChoice === 'LTL'
                                                    ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-500'
                                                    : 'text-gray-600 hover:text-gray-800'}`}
                                        >
                                            LTL
                                        </button>
                                    </div>
                                    {!shipmentModeChoice && (
                                        <p className="mt-1.5 text-xs text-amber-700">
                                            This ASIN is approved for either UPS or LTL &mdash; choose one before saving so
                                            the right weight ceiling applies.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* ---- Task 67: Single-unit (unit = box) view -- units/box locked to
                                1, dims + weight prefilled from the Amazon catalog dims when known
                                (still editable for corrections), no carton picker. ---- */}
                            {packagingStyleChoice === 'UNIT' && (
                                <>
                                    {packagingModalDerived ? (
                                        <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                                            Auto-filled from Amazon catalog dims
                                            ({packagingModalDerived.dimsFrom === 'PACKAGE' ? 'package' : 'item'} measurements):
                                            {' '}{packagingModalDerived.boxLengthIn}×{packagingModalDerived.boxWidthIn}×{packagingModalDerived.boxHeightIn} in
                                            · {fmtLbsShort(packagingModalDerived.boxWeightLbs)} lb. Edit below if the real package differs.
                                        </p>
                                    ) : (
                                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                                            Amazon has no usable package dims for this ASIN &mdash; enter the unit&rsquo;s own
                                            package dimensions and weight manually.
                                        </p>
                                    )}
                                    <div>
                                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                            The unit&rsquo;s own package
                                        </h4>
                                        <div className="grid grid-cols-4 gap-2">
                                            {[['boxLengthIn', 'L'], ['boxWidthIn', 'W'], ['boxHeightIn', 'H']].map(([field, label]) => (
                                                <div key={field}>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">{label} (in)</label>
                                                    <input
                                                        type="number" min="0.01" step="0.1"
                                                        value={packagingForm[field]}
                                                        onChange={e => updatePackagingField(field, e.target.value)}
                                                        className={`block w-full rounded-md shadow-sm text-sm ${
                                                            packagingFieldErrors[field] && (packagingAttemptedSubmit || packagingForm[field] !== '') ? 'border-red-400' : 'border-gray-300'
                                                        }`}
                                                    />
                                                </div>
                                            ))}
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Weight (lb)</label>
                                                <input
                                                    type="number" min="0.01" step="0.1"
                                                    value={packagingForm.unitWeightLbs}
                                                    onChange={e => updatePackagingField('unitWeightLbs', e.target.value)}
                                                    className={`block w-full rounded-md shadow-sm text-sm ${
                                                        packagingFieldErrors.unitWeightLbs && (packagingAttemptedSubmit || packagingForm.unitWeightLbs !== '') ? 'border-red-400' : 'border-gray-300'
                                                    }`}
                                                />
                                            </div>
                                        </div>
                                        <p className="mt-1.5 text-[10px] text-gray-400">
                                            Units per box is locked to 1 &mdash; the unit&rsquo;s manufacturer packaging IS the shipping box.
                                        </p>
                                    </div>
                                    <div className={`rounded-lg border px-3 py-3 ${
                                        packagingFieldErrors.loaded ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                                    }`}>
                                        {packagingLoadedLbs != null && !packagingFieldErrors.boxLengthIn && !packagingFieldErrors.boxWidthIn && !packagingFieldErrors.boxHeightIn ? (
                                            <>
                                                <p className={`text-sm font-semibold ${packagingFieldErrors.loaded ? 'text-red-700' : 'text-gray-900'}`}>
                                                    Unit ships as its own box &mdash; 1/box · {packagingForm.boxLengthIn}×{packagingForm.boxWidthIn}×{packagingForm.boxHeightIn} · {fmtLbsShort(packagingLoadedLbs)} lb
                                                </p>
                                                {packagingFieldErrors.loaded && (
                                                    <p className="text-xs text-red-600 mt-0.5">{packagingFieldErrors.loaded}</p>
                                                )}
                                                {packagingPopoverQty > 0 && !packagingFieldErrors.loaded && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        {packagingPopoverQty} units → {packagingPopoverQty} box{packagingPopoverQty !== 1 ? 'es' : ''}
                                                        {' '}· total {fmtLbsShort(packagingPopoverQty * packagingLoadedLbs)} lb
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            <p className="text-xs text-gray-400">
                                                Enter the unit&rsquo;s package dimensions and weight &mdash; each unit ships as one box.
                                            </p>
                                        )}
                                    </div>
                                </>
                            )}

                            {packagingStyleChoice === 'CARTON' && packagingModalLegacyOnly && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                                    This ASIN&rsquo;s current value ({packagingSummaryLine(packagingModalActivePkg)}) predates
                                    the unit-weight model &mdash; enter the unit weight and pick a box below to replace it
                                    with a computed configuration.
                                </p>
                            )}

                            {/* ---- Carton style (the pre-task-67 flow, unchanged) ---- */}
                            {packagingStyleChoice === 'CARTON' && (<>
                            {/* ---- Step 1: this ASIN's numbers ---- */}
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                    Step 1 &middot; This ASIN&rsquo;s shipment configuration
                                </h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Unit weight</label>
                                        <div className="relative">
                                            <input
                                                type="number" min="0.01" step="0.1"
                                                value={packagingForm.unitWeightLbs}
                                                onChange={e => updatePackagingField('unitWeightLbs', e.target.value)}
                                                className={`block w-full rounded-md shadow-sm text-sm pr-8 ${
                                                    packagingFieldErrors.unitWeightLbs && (packagingAttemptedSubmit || packagingForm.unitWeightLbs !== '') ? 'border-red-400' : 'border-gray-300'
                                                }`}
                                            />
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">lb</span>
                                        </div>
                                        {(packagingAttemptedSubmit || packagingForm.unitWeightLbs !== '') && packagingFieldErrors.unitWeightLbs && (
                                            <p className="mt-0.5 text-xs text-red-600">{packagingFieldErrors.unitWeightLbs}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Units per box</label>
                                        <input
                                            type="number" min="1"
                                            value={packagingForm.unitsPerBox}
                                            onChange={e => updatePackagingField('unitsPerBox', e.target.value)}
                                            className={`block w-full rounded-md shadow-sm text-sm ${
                                                packagingFieldErrors.unitsPerBox && (packagingAttemptedSubmit || packagingForm.unitsPerBox !== '') ? 'border-red-400' : 'border-gray-300'
                                            }`}
                                        />
                                        {(packagingAttemptedSubmit || packagingForm.unitsPerBox !== '') && packagingFieldErrors.unitsPerBox && (
                                            <p className="mt-0.5 text-xs text-red-600">{packagingFieldErrors.unitsPerBox}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* ---- Step 2: the shared carton ---- */}
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                    Step 2 &middot; Box (shared across all ASINs)
                                </h4>
                                <div className="space-y-1.5">
                                    {boxCatalog.map(box => renderCatalogBoxCard(box))}
                                    {boxCatalog.length === 0 && !newBoxOpen && (
                                        <p className="text-xs text-gray-400 italic">No boxes in the catalog yet &mdash; create the first one below.</p>
                                    )}

                                    {/* "New box…" -- visually distinct (dashed, + icon), expands the inline form.
                                        2026-09-01: the same form is the EDIT form when a card's pencil opened it
                                        (editingBoxId set) -- prefilled, name locked, saves via the same upsert. */}
                                    <div
                                        onClick={() => {
                                            if (!newBoxOpen) {
                                                setEditingBoxId(null);
                                                setNewBoxForm({ name: '', boxLengthIn: '', boxWidthIn: '', boxHeightIn: '', tareWeightLbs: '' });
                                                setBoxError(null);
                                                setNewBoxOpen(true);
                                                setSelectedBoxId(null);
                                            }
                                        }}
                                        className={`rounded-lg border-2 border-dashed px-3 py-2.5 transition-colors ${
                                            newBoxOpen ? 'border-indigo-500 bg-indigo-50/40' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50 cursor-pointer'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center ${newBoxOpen ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                                {editingBoxId != null ? <PencilIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
                                            </span>
                                            <span className="font-semibold text-sm text-gray-900">
                                                {editingBoxId != null ? <>Edit box &mdash; {newBoxForm.name}</> : <>New box&hellip;</>}
                                            </span>
                                        </div>
                                        {newBoxOpen && (
                                            <div className="mt-3 space-y-2" onClick={e => e.stopPropagation()}>
                                                <input
                                                    type="text" maxLength={60}
                                                    value={newBoxForm.name}
                                                    onChange={e => updateNewBoxField('name', e.target.value)}
                                                    placeholder="Box name (e.g. Large)"
                                                    disabled={editingBoxId != null}
                                                    title={editingBoxId != null ? 'The name identifies this box everywhere — renaming would create a new box' : undefined}
                                                    className="block w-full rounded-md border-gray-300 shadow-sm text-sm disabled:bg-gray-100 disabled:text-gray-500"
                                                />
                                                <div className="grid grid-cols-4 gap-2">
                                                    {[['boxLengthIn', 'L'], ['boxWidthIn', 'W'], ['boxHeightIn', 'H']].map(([field, label]) => (
                                                        <div key={field} className="relative">
                                                            <input
                                                                type="number" min="0.01" step="0.1"
                                                                value={newBoxForm[field]}
                                                                onChange={e => updateNewBoxField(field, e.target.value)}
                                                                placeholder={label}
                                                                className="block w-full rounded-md border-gray-300 shadow-sm text-sm pr-6"
                                                            />
                                                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">in</span>
                                                        </div>
                                                    ))}
                                                    <div className="relative">
                                                        <input
                                                            type="number" min="0.01" step="0.1"
                                                            value={newBoxForm.tareWeightLbs}
                                                            onChange={e => updateNewBoxField('tareWeightLbs', e.target.value)}
                                                            placeholder="tare"
                                                            title="Weight of the EMPTY box"
                                                            className="block w-full rounded-md border-gray-300 shadow-sm text-sm pr-6"
                                                        />
                                                        <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">lb</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-[10px] text-gray-400">Tare = the empty box&rsquo;s own weight. Saved to the shared catalog for every ASIN.</p>
                                                    <button
                                                        onClick={saveNewBoxToCatalog}
                                                        disabled={newBoxSaving}
                                                        className="flex-shrink-0 px-2.5 py-1.5 text-xs rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                                                    >
                                                        {newBoxSaving ? 'Saving…' : (editingBoxId != null ? 'Update box' : 'Save box')}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {packagingAttemptedSubmit && packagingFieldErrors.box && (
                                    <p className="mt-1.5 text-xs text-red-600">{packagingFieldErrors.box}</p>
                                )}
                                {/* 2026-09-01: box save/delete failures render HERE -- the page-top
                                    toast sits behind this full-screen modal, so refusals (delete's
                                    "box is in use by N ASIN configuration(s)") looked like dead clicks. */}
                                {boxError && (
                                    <p className="mt-1.5 text-xs text-red-600">{boxError}</p>
                                )}
                                {boxNote && <p className="mt-2 text-xs text-gray-500 italic">{boxNote}</p>}
                            </div>

                            {/* ---- The computed line -- prominent, owner's formula spelled out ---- */}
                            <div className={`rounded-lg border px-3 py-3 ${
                                packagingFieldErrors.loaded ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                            }`}>
                                {packagingLoadedLbs != null && selectedBox ? (
                                    <>
                                        <p className={`text-sm font-semibold ${packagingFieldErrors.loaded ? 'text-red-700' : 'text-gray-900'}`}>
                                            {parseInt(packagingForm.unitsPerBox, 10)} unit{parseInt(packagingForm.unitsPerBox, 10) !== 1 ? 's' : ''} × {fmtLbsShort(parseFloat(packagingForm.unitWeightLbs))} lb
                                            {' '}+ {fmtLbsShort(selectedBox.tareWeightLbs)} lb box = {fmtLbsShort(packagingLoadedLbs)} lb per box
                                        </p>
                                        {packagingFieldErrors.loaded && (
                                            <p className="text-xs text-red-600 mt-0.5">Over the 150 lb parcel ceiling &mdash; reduce units per box or use a lighter box.</p>
                                        )}
                                        {packagingPopoverQty && packagingModalTotals && !packagingFieldErrors.loaded && (
                                            <p className="text-xs text-gray-600 mt-1">
                                                {packagingPopoverQty} units → {packagingModalTotals.boxCount} box{packagingModalTotals.boxCount !== 1 ? 'es' : ''}
                                                {packagingModalTotals.totalLbs != null && <> · total {fmtLbsShort(packagingModalTotals.totalLbs)} lb</>}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-xs text-gray-400">
                                        Enter the unit weight, units per box, and pick a box &mdash; the loaded box weight is
                                        computed for you (units × unit weight + empty-box weight).
                                    </p>
                                )}
                            </div>
                            </>)}
                        </div>

                        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-lg">
                            <button onClick={closePackagingPopover} className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
                                Cancel
                            </button>
                            <button
                                onClick={() => applyPackaging(true)}
                                disabled={!packagingFormValid || packagingSaving}
                                className="px-3 py-2 text-sm rounded-md border border-indigo-600 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                            >
                                {packagingSaving ? 'Saving...' : `Use and make default for ${packagingPopoverAsin}`}
                            </button>
                            {!primaryActionOnly && (
                                <button
                                    onClick={() => applyPackaging(false)}
                                    disabled={!packagingFormValid || packagingSaving}
                                    className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Use for this shipment
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )
    );
}
